import { AppProps } from '../../../@types/AppProps';
import { Valuation } from '../../../@types/Valuation';
import { ValuationState } from '../../../@types/ValuationState';
import ButtonArrowRight from '../../../Components/Button/ButtonArrowRight';
import HtmlNode from '../../../Components/Extension/HtmlNode';
import InputField from '../../../Components/Input/InputField';
import InputSelect from '../../../Components/Input/InputSelect';
import InputTextarea from '../../../Components/Input/InputTextarea';
import createLead from '../../../Http/createLead';
import Alert from '../../../Templates/Alert';
import { createLeadBody } from './utils';
import validation from './validation';

const INPUT_FNAME = 'input-fname';
const INPUT_FNAME_NODE = `${INPUT_FNAME}-node`;
const INPUT_LNAME = 'input-lname';
const INPUT_LNAME_NODE = `${INPUT_LNAME}-node`;
const INPUT_EMAIL = 'input-email';
const INPUT_EMAIL_NODE = `${INPUT_EMAIL}-node`;
const INPUT_PHONE = 'input-phone';
const INPUT_PHONE_NODE = `${INPUT_PHONE}-node`;
const INPUT_DESCRIPTION = 'input-description';
const INPUT_DESCRIPTION_NODE = `${INPUT_DESCRIPTION}-node`;
const INPUT_BRANCH = 'input-branch';
const INPUT_BRANCH_NODE = `${INPUT_BRANCH}-node`;

const BUTTON_SEND = 'button-send';
const BUTTON_SEND_NODE = `${BUTTON_SEND}-node`;

interface ContactStateLocalValidation {
  fname: boolean;
  lname: boolean;
  email: boolean;
  phone: boolean;
  branchId: boolean;
  description: boolean;
}

interface ContactStateLocal {
  value: {
    fname: string;
    lname: string;
    email: string;
    phone: string;
    branchId: string;
    description: string;
  };
  validation: ContactStateLocalValidation;
  interaction: ContactStateLocalValidation;
}

type NameTypes = keyof ContactStateLocal['value'];

const isValid = (v: ContactStateLocalValidation) =>
  v.fname && v.lname && v.email && v.phone && v.branchId && v.description;

const createState = (_value: ValuationState): ContactStateLocal => {
  const value = { ..._value };
  return {
    value,
    validation: {
      fname: validation.fname(value.fname),
      lname: validation.lname(value.lname),
      email: validation.email(value.email),
      phone: validation.phone(value.phone),
      branchId: validation.branchId(value.branchId),
      description: validation.description(value.description),
    },
    interaction: {
      fname: value.fname ? true : false,
      lname: value.lname ? true : false,
      email: value.email ? true : false,
      phone: value.phone ? true : false,
      branchId: false,
      description: value.description ? true : false,
    },
  };
};

interface ContactProps {
  settings: AppProps;
  valuation: Valuation;
  state: ValuationState;
  updateContactSilent: (state: ValuationState) => void;
  onProceed: (state: ValuationState) => void;
}

class Contact extends HtmlNode {
  private readonly props: ContactProps;
  private state: ContactStateLocal;
  private requestError = false;
  private contexts: {
    fname?: InputField;
    lname?: InputField;
    email?: InputField;
    phone?: InputField;
    branchId?: InputField;
    description?: InputTextarea;
    buttonSend?: ButtonArrowRight;
  } = {};

  constructor(element: HTMLElement | null, props: ContactProps) {
    super(element);
    this.props = props;
    this.state = createState(this.props.state);

    this.render();
  }

  private onChange(e: Event) {
    const currentTarget = e.currentTarget as HTMLInputElement;
    const name = currentTarget.name as NameTypes;
    const value = currentTarget.value;

    this.state.value[name] = value;
    this.state.validation[name] = validation[name](value);
    this.updateUiError(name);
    this.updateProceedButton();
  }

  private onBlur(e: Event) {
    const currentTarget = e.currentTarget as HTMLInputElement;
    const name = currentTarget.name as NameTypes;

    this.state.interaction[name] = true;
    this.state.validation[name] = validation[name](this.state.value[name]);
    this.updateUiError(name);
    this.updateProceedButton();
    this.props.updateContactSilent({
      ...this.props.state,
      ...this.state.value,
    });
  }

  private onChangeSelect(e: Event) {
    const currentTarget = e.currentTarget as HTMLSelectElement;
    const name = currentTarget.name as 'branchId';
    this.state.interaction[name] = true;
    this.state.validation[name] = validation[name](this.state.value[name]);
    this.state.value[name] = currentTarget.value;
    this.updateUiError(name);
    this.updateProceedButton();
    this.render();
  }

  private onKeyDown(e: KeyboardEvent) {
    if (e.key == 'Enter') {
      this.onBlur(e);
    }
  }

  private updateUiError(name: NameTypes) {
    this.contexts[name]?.setError(this.state.interaction[name] && !this.state.validation[name]);
  }

  private updateProceedButton() {
    this.contexts.buttonSend?.disabled(!isValid(this.state.validation));
  }

  private async send() {
    this.requestError = false;
    this.render();
    try {
      this.contexts.buttonSend?.loading(true);

      const _valuationState = { ...this.props.state, ...this.state.value };
      const body = createLeadBody(_valuationState, this.props.valuation, this.props.settings);

      await createLead(this.props.settings.url, body);
      this.props.onProceed({ ...this.props.state, ...this.state.value });
    } catch (e) {
      this.contexts.buttonSend?.loading(false);
      this.requestError = true;
      this.render();
    }
  }

  render() {
    const selectedBranchName = this.props.settings.branches.find(
      (x) => x.id === this.state.value.branchId
    )?.name;

    this.node.innerHTML = `
      <div class="waykevaluation-stack waykevaluation-stack--4">
        <h4 class="waykevaluation-heading waykevaluation-heading--3">Vill du sälja bilen?</h4>
        <div class="waykevaluation-content">
          <p class="waykevaluation-content__p">Sälj din bil till oss! Fyll i intresseanmälan så kontaktar vi dig. Du binder dig inte till något genom att anmäla intresse.</p>
        </div>
      </div>

      <div class="waykevaluation-stack waykevaluation-stack--4">
        <h5 class="waykevaluation-heading waykevaluation-heading--4">Kontaktuppgifter</h5>
        <div class="waykevaluation-stack waykevaluation-stack--3" id="${INPUT_FNAME_NODE}"></div>
        <div class="waykevaluation-stack waykevaluation-stack--3" id="${INPUT_LNAME_NODE}"></div>
        <div class="waykevaluation-stack waykevaluation-stack--3" id="${INPUT_EMAIL_NODE}"></div>
        <div class="waykevaluation-stack waykevaluation-stack--3" id="${INPUT_PHONE_NODE}"></div>
      </div>

      <div class="waykevaluation-stack waykevaluation-stack--4">
        <hr class="waykevaluation-separator" />
      </div>

      <div class="waykevaluation-stack waykevaluation-stack--4">
        <h5 class="waykevaluation-heading waykevaluation-heading--4">Vill du lämna någon mer information om din bil?</h5>
        ${
          (this.props.settings.branches?.length || 0) > 1
            ? `
              <div class="waykevaluation-stack waykevaluation-stack--3" id="${INPUT_BRANCH_NODE}"></div>
            `
            : ''
        }
        <div class="waykevaluation-stack waykevaluation-stack--3" id="${INPUT_DESCRIPTION_NODE}"></div>
      </div>

      <div class="waykevaluation-stack waykevaluation-stack--4">
        <hr class="waykevaluation-separator" />
      </div>

      <div class="waykevaluation-stack waykevaluation-stack--4">
        <h5 class="waykevaluation-heading waykevaluation-heading--4">Vad är nästa steg?</h5>
        <div class="waykevaluation-content">
          <p class="waykevaluation-content__p">Vi tar kontakt med dig för att tillsammans reda ut detaljerna kring din försäljning. Kom ihåg att du inte binder dig till något.</p>
        </div>
      </div>

      <div class="waykevaluation-stack waykevaluation-stack--4">
        ${
          this.requestError
            ? Alert({
                tone: 'error',
                children: 'Det gick inte att skicka iväg intresseanmälan, försök igen.',
              })
            : ''
        }
        <div class="waykevaluation-stack waykevaluation-stack--2" id="${BUTTON_SEND_NODE}"></div>
        <div class="waykevaluation-stack waykevaluation-stack--2">
          <div class="waykevaluation-disclaimer">Ditt registreringsnummer och dina personuppgifter behandlas av vår värderingsleveranstör Wayke och kommer delas med ${selectedBranchName}. <a href="https://www.wayke.se/personuppgiftspolicy-wayke" title="Läs mer om hur ditt registreringsnummer och dina personuppgifter behandlas" target="_blank" rel="noopener noreferrer" class="waykevaluation-link">Läs mer</a></div>
        </div>
      </div>
    `;

    this.contexts.fname = new InputField(
      this.node.querySelector<HTMLDivElement>(`#${INPUT_FNAME_NODE}`),
      {
        title: 'Förnamn',
        value: this.state.value.fname,
        id: INPUT_FNAME,
        error: this.state.interaction.fname && !this.state.validation.fname,
        errorMessage: 'Du måste ange ditt förnamn',
        name: 'fname',
        autocomplete: 'off',
        placeholder: 'Ange förnamn',
        onChange: (e) => this.onChange(e),
        onBlur: (e) => this.onBlur(e),
        onKeyDown: (e) => this.onKeyDown(e),
      }
    );

    this.contexts.lname = new InputField(
      this.node.querySelector<HTMLDivElement>(`#${INPUT_LNAME_NODE}`),
      {
        title: 'Efternamn',
        value: this.state.value.lname,
        id: INPUT_LNAME,
        error: this.state.interaction.lname && !this.state.validation.lname,
        errorMessage: 'Du måste ange ditt efternamn',
        name: 'lname',
        autocomplete: 'off',
        placeholder: 'Ange efternamn',
        onChange: (e) => this.onChange(e),
        onBlur: (e) => this.onBlur(e),
        onKeyDown: (e) => this.onKeyDown(e),
      }
    );

    this.contexts.email = new InputField(
      this.node.querySelector<HTMLDivElement>(`#${INPUT_EMAIL_NODE}`),
      {
        title: 'E-postadress',
        value: this.state.value.email,
        id: INPUT_EMAIL,
        error: this.state.interaction.email && !this.state.validation.email,
        errorMessage: 'Du måste ange din e-postadress',
        name: 'email',
        autocomplete: 'off',
        placeholder: 'Ange e-postadress',
        onChange: (e) => this.onChange(e),
        onBlur: (e) => this.onBlur(e),
        onKeyDown: (e) => this.onKeyDown(e),
      }
    );

    this.contexts.phone = new InputField(
      this.node.querySelector<HTMLDivElement>(`#${INPUT_PHONE_NODE}`),
      {
        title: 'Telefonnummer (valfritt)',
        value: this.state.value.phone,
        id: INPUT_PHONE,
        error: this.state.interaction.phone && !this.state.validation.phone,
        errorMessage: 'Du måste ange ditt telefonnummer',
        name: 'phone',
        autocomplete: 'off',
        placeholder: 'Ange telefonnummer',
        onChange: (e) => this.onChange(e),
        onBlur: (e) => this.onBlur(e),
        onKeyDown: (e) => this.onKeyDown(e),
      }
    );

    if ((this.props.settings.branches.length || 0) > 1) {
      new InputSelect(this.node.querySelector<HTMLDivElement>(`#${INPUT_BRANCH_NODE}`), {
        id: INPUT_BRANCH,
        title: 'Anläggning',
        name: 'branchId',
        value: this.state.value.branchId,
        options: this.props.settings.branches.map((b) => ({ value: b.id, title: b.name })),
        placeholder: 'Välj anläggning',
        information:
          'Vi har flera anläggningar i Sverige. Välj den anläggning som du vill komma i kontakt med.',
        onChange: (e) => this.onChangeSelect(e),
      });
    }

    this.contexts.description = new InputTextarea(
      this.node.querySelector<HTMLDivElement>(`#${INPUT_DESCRIPTION_NODE}`),
      {
        title: 'Bra att känna till om min bil (valfritt)',
        value: this.state.value.description,
        id: INPUT_DESCRIPTION,
        error: this.state.interaction.description && !this.state.validation.description,
        errorMessage: 'Du behöver ange en beskrivning',
        name: 'description',
        autocomplete: 'off',
        placeholder:
          'Är det något mer om din bil som du vill berätta för oss? Något som kan påverka värdet så som servicehistorik, extrautrustning, vinterdäck?',
        onChange: (e) => this.onChange(e),
        onBlur: (e) => this.onBlur(e),
      }
    );

    this.contexts.buttonSend = new ButtonArrowRight(
      this.node.querySelector<HTMLDivElement>(`#${BUTTON_SEND_NODE}`),
      {
        title: 'Skicka in intresseanmälan',
        disabled: !isValid(this.state.validation),
        onClick: () => this.send(),
      }
    );
  }
}

export default Contact;
